import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/ui/Button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { RadioButton } from 'primereact/radiobutton';
import { Skeleton } from 'primereact/skeleton';

import { useGetScenes, useActivateScene } from '../../../queries/scenes';
import { useGetHardwareByRole, useMatrixRoute } from '../../../queries/hardware';
import {
  useGetBirthdaySessions,
  useGetBirthdayAudioSettings,
  useStartBirthdaySession,
  useEndBirthdaySession,
} from '../../../queries/birthday';
import { useHasHardware } from '../../../hooks/useHasHardware';
import FeatureUnavailable from '../../../components/activation/FeatureUnavailable';
import { useEntitlements } from '../../../hooks/useEntitlements';
import { useCustomToast } from '../../Contexts/ToastContext';

/**
 * Scenes (one-tap presets), matrix routing (collapsible advanced),
 * birthday quick-start. The most concrete monetization surface — sits
 * on the right rail next to the screen grid.
 */
export default function QuickActions({ screens }) {
  const { t } = useTranslation();
  const { showToast } = useCustomToast();
  const fullPtn = useHasHardware();   // matrix routing is a PTN-only surface
  const { has } = useEntitlements();
  const hwOn = has('hardware_control');
  const bdayOn = has('birthday_mode');

  /* ── Scenes ──────────────────────────────────────────────────────── */
  const { data: sceneData, isLoading: scenesLoading } = useGetScenes({}, { enabled: hwOn });
  const scenes = Array.isArray(sceneData?.data) ? sceneData.data : Array.isArray(sceneData) ? sceneData : [];
  const activateMut = useActivateScene();
  const [activeSceneId, setActiveSceneId] = useState(null);

  // Scene names are stored "🎵 Café Mode" — split the leading emoji so we can
  // show it big on a mode card. The "🔊 Toggle Audio" scene is pulled out as a
  // prominent full-width action (it flips the active player's audio, no re-pick).
  const splitEmoji = (name = '') => {
    const i = name.indexOf(' ');
    return i > 0 ? { emoji: name.slice(0, i), label: name.slice(i + 1) } : { emoji: '', label: name };
  };
  const isToggleScene = (s) => /🔊|toggle|بدّل/i.test(s?.name ?? '');
  const toggleScene = useMemo(() => scenes.find(isToggleScene), [scenes]);
  const modeScenes  = useMemo(() => scenes.filter((s) => !isToggleScene(s)), [scenes]);

  const activate = (scene) => {
    activateMut.mutate(scene.id, {
      onSuccess: () => {
        setActiveSceneId(scene.id);
        showToast('success', t('remote_control.title'),
          t('remote_control.toasts.scene_activated', { name: scene.name }));
      },
    });
  };

  /* ── Matrix routing ──────────────────────────────────────────────── */
  // Role-filtered lists. The matrix endpoint targets the *Control Box*
  // (the IP-addressable PTN unit) — not the encoder itself. So we keep
  // the encoder's parent_device_id around to pick the right control box.
  const { data: encData } = useGetHardwareByRole('encoder', {}, { enabled: hwOn });
  const { data: decData } = useGetHardwareByRole('decoder', {}, { enabled: hwOn });

  const encoderRows = useMemo(
    () => (Array.isArray(encData?.data) ? encData.data : Array.isArray(encData) ? encData : []),
    [encData]
  );
  const decoderRows = useMemo(
    () => (Array.isArray(decData?.data) ? decData.data : Array.isArray(decData) ? decData : []),
    [decData]
  );

  const encoderOpts = useMemo(
    () => encoderRows.map((e) => ({
      label: e.port_number ? `${e.name} (port ${e.port_number})` : e.name,
      value: e.id,
    })),
    [encoderRows]
  );

  const [advOpen,   setAdvOpen]   = useState(false);
  const [selEncId,  setSelEncId]  = useState(null);     // encoder.id (UI state)
  const [selTarget, setSelTarget] = useState('all');    // decoder.id OR literal 'all'
  const [selSignal, setSelSignal] = useState('va');     // default to video+audio per spec

  // Resolve the chain encoder.id → encoder row → parent (control box) → port numbers.
  const selEnc        = useMemo(() => encoderRows.find((e) => e.id === selEncId), [encoderRows, selEncId]);
  const controlBoxId  = selEnc?.parent_device_id ?? null;
  const encoderPort   = selEnc?.port_number ?? null;
  const selDecoder    = useMemo(() => decoderRows.find((d) => d.id === selTarget), [decoderRows, selTarget]);
  const targetPort    = selTarget === 'all' ? 'all' : (selDecoder?.port_number ?? null);

  // useMatrixRoute is keyed on the Control Box id — that's the device the
  // backend sends the >Switch command to.
  const matrixMut = useMatrixRoute(controlBoxId);

  // Decoder dropdown: filter to decoders that live on the same Control Box
  // as the selected encoder (a venue can have multiple Control Boxes).
  const targetOpts = useMemo(() => {
    const base = [{ label: t('remote_control.matrix.all_targets'), value: 'all' }];
    if (!controlBoxId) return base;
    return [
      ...base,
      ...decoderRows
        .filter((d) => d.parent_device_id === controlBoxId)
        .map((d) => ({
          label: d.port_number ? `${d.name} (port ${d.port_number})` : d.name,
          value: d.id,
        })),
    ];
  }, [decoderRows, controlBoxId, t]);

  const doRoute = () => {
    if (!selEncId || !controlBoxId) {
      showToast('warn', t('remote_control.title'), t('remote_control.matrix.no_encoders'));
      return;
    }
    if (!encoderPort) {
      showToast('warn', t('remote_control.title'), 'Encoder is missing a port number.');
      return;
    }
    if (selTarget !== 'all' && !targetPort) {
      showToast('warn', t('remote_control.title'), 'Decoder is missing a port number.');
      return;
    }
    matrixMut.mutate(
      { input: encoderPort, output: selTarget === 'all' ? 'all' : targetPort, type: selSignal },
      {
        onSuccess: () => showToast('success', t('remote_control.title'),
          t('remote_control.toasts.routed', {
            source: selEnc?.name ?? '?',
            target: selTarget === 'all'
              ? t('remote_control.matrix.all_targets')
              : (selDecoder?.name ?? '?'),
          })
        ),
      }
    );
  };

  /* ── Birthday ────────────────────────────────────────────────────── */
  const { data: birthdaySessionsData } = useGetBirthdaySessions({ status: 'ACTIVE' }, { enabled: bdayOn });
  const activeBirthday = useMemo(() => {
    const rows = Array.isArray(birthdaySessionsData?.data) ? birthdaySessionsData.data
              : Array.isArray(birthdaySessionsData) ? birthdaySessionsData : [];
    return rows.find((s) => s.status === 'ACTIVE') ?? null;
  }, [birthdaySessionsData]);

  const { data: audioData } = useGetBirthdayAudioSettings({ enabled: bdayOn });
  const audioOptions = useMemo(() => {
    const a = audioData?.data ?? audioData ?? {};
    // Schema is template-based: { template_arabic_url, template_english_url, template_calm_url, template_party_url }
    return Object.entries(a)
      .filter(([k, v]) => k.startsWith('template_') && !!v)
      .map(([k]) => {
        const name = k.replace('template_', '').replace('_url', '');
        return { label: name.charAt(0).toUpperCase() + name.slice(1), value: name };
      });
  }, [audioData]);

  const startMut = useStartBirthdaySession();
  const endMut   = useEndBirthdaySession();

  const [bdayDialog, setBdayDialog] = useState(false);
  const [bdayForm, setBdayForm] = useState({
    birthday_person_name: '', template: 'arabic', duration_minutes: 5, device_ids: [],
  });

  const startBirthday = () => {
    if (!bdayForm.birthday_person_name || bdayForm.device_ids.length === 0) {
      showToast('warn', t('remote_control.title'), t('remote_control.channels.no_screens_selected'));
      return;
    }
    startMut.mutate(bdayForm, {
      onSuccess: () => {
        showToast('success', t('remote_control.title'),
          t('remote_control.toasts.birthday_started', { guest: bdayForm.birthday_person_name }));
        setBdayDialog(false);
        setBdayForm({ birthday_person_name: '', template: 'arabic', duration_minutes: 5, device_ids: [] });
      },
    });
  };

  const endBirthday = () => {
    if (!activeBirthday) return;
    endMut.mutate(activeBirthday.id, {
      onSuccess: () =>
        showToast('success', t('remote_control.title'), t('remote_control.toasts.birthday_ended')),
    });
  };

  /* ── render ──────────────────────────────────────────────────────── */
  return (
    <div className="remote-section">
      <div className="remote-section-title">
        <i className="pi pi-bolt" /> {t('remote_control.sections.quick_actions')}
      </div>

      {/* Scenes */}
      <div className="mb-3">
        <div className="remote-receiver-name">{t('remote_control.scenes.title')}</div>
        {!hwOn ? (
          <FeatureUnavailable compact />
        ) : scenesLoading ? (
          <Skeleton height="5rem" />
        ) : scenes.length === 0 ? (
          <p className="small text-muted mb-0">{t('remote_control.scenes.empty')}</p>
        ) : (
          <>
            {/* Mode cards — one tap = one café "mode" */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
              {modeScenes.map((scene) => {
                const { emoji, label } = splitEmoji(scene.name);
                const active = scene.id === activeSceneId;
                return (
                  <button
                    key={scene.id}
                    onClick={() => activate(scene)}
                    disabled={activateMut.isLoading}
                    title={scene.name}
                    style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center',
                      justifyContent: 'center', gap: '0.35rem', minHeight: '5.25rem',
                      padding: '0.75rem 0.5rem', borderRadius: '0.9rem', cursor: 'pointer',
                      textAlign: 'center', transition: 'all .15s',
                      border: active ? '2px solid #10b981' : '1px solid var(--pp-border)',
                      background: active
                        ? 'linear-gradient(135deg, rgba(16,185,129,.16), rgba(5,150,105,.10))'
                        : 'var(--pp-bg-tertiary)',
                      boxShadow: active ? '0 2px 10px rgba(16,185,129,.20)' : 'none',
                    }}
                  >
                    <span style={{ fontSize: '1.7rem', lineHeight: 1 }}>{emoji || '🎬'}</span>
                    <span className="fw-semibold" style={{ fontSize: '.8rem', lineHeight: 1.15 }}>{label}</span>
                    {active && (
                      <span className="remote-audio-pill remote-audio-on" style={{ fontSize: '.65rem' }}>
                        {t('remote_control.scenes.active')}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Toggle Audio — prominent, flips match commentary <-> music */}
            {toggleScene && (
              <button
                onClick={() => activate(toggleScene)}
                disabled={activateMut.isLoading}
                style={{
                  width: '100%', marginTop: '0.6rem', padding: '0.7rem 1rem',
                  borderRadius: '0.9rem', cursor: 'pointer', fontWeight: 700,
                  border: 'none', color: '#fff', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: '0.5rem', fontSize: '.95rem',
                  background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                  boxShadow: '0 2px 10px rgba(79,70,229,.25)',
                }}
              >
                🔊 {splitEmoji(toggleScene.name).label}
              </button>
            )}
          </>
        )}
      </div>

      {/* Birthday (hidden when the package explicitly lacks birthday_mode) */}
      {bdayOn && (
      <div className="mb-3">
        <div className="remote-receiver-name">{t('remote_control.birthday.title')}</div>
        {activeBirthday ? (
          <button
            className="remote-birthday-btn remote-birthday-btn-active"
            onClick={endBirthday}
            disabled={endMut.isLoading}
          >
            🛑 {t('remote_control.birthday.end')}
          </button>
        ) : (
          <button
            className="remote-birthday-btn"
            onClick={() => setBdayDialog(true)}
          >
            🎂 {t('remote_control.birthday.start')}
          </button>
        )}
        {activeBirthday && (
          <div className="small text-muted mt-1">
            {t('remote_control.birthday.active_session', {
              guest: activeBirthday.birthday_person_name,
            })}
          </div>
        )}
      </div>
      )}

      {/* Advanced: matrix routing — PTN-only, hidden in Simple mode */}
      {fullPtn && hwOn && (
      <div className="mb-2">
        <button
          className="btn btn-link p-0 small fw-semibold text-decoration-none"
          onClick={() => setAdvOpen((x) => !x)}
        >
          <i className={`pi ${advOpen ? 'pi-chevron-down' : 'pi-chevron-right'} me-1`} />
          {t('remote_control.matrix.title')}
        </button>

        {advOpen && (
          <div className="d-flex flex-column gap-2 mt-2 p-2 rounded" style={{ background: 'var(--pp-bg-tertiary)', border: '1px solid var(--pp-border)' }}>
            <label className="small fw-semibold mb-0">{t('remote_control.matrix.source')}</label>
            <Dropdown
              value={selEncId} options={encoderOpts} onChange={(e) => setSelEncId(e.value)}
              placeholder={t('remote_control.matrix.no_encoders')}
              disabled={encoderOpts.length === 0}
            />

            <label className="small fw-semibold mb-0 mt-2">{t('remote_control.matrix.target')}</label>
            <Dropdown value={selTarget} options={targetOpts} onChange={(e) => setSelTarget(e.value)} />

            <label className="small fw-semibold mb-0 mt-2">{t('remote_control.matrix.signal')}</label>
            <div className="d-flex gap-3">
              {[
                { v: 'v',  label: t('remote_control.matrix.video') },
                { v: 'a',  label: t('remote_control.matrix.audio') },
                { v: 'va', label: t('remote_control.matrix.both')  },
              ].map((opt) => (
                <label key={opt.v} className="d-flex align-items-center gap-1 small">
                  <RadioButton
                    name="rc-sig" value={opt.v}
                    checked={selSignal === opt.v}
                    onChange={(e) => setSelSignal(e.value)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>

            <Button
              label={t('remote_control.matrix.route')}
              icon="pi pi-arrow-right"
              loading={matrixMut.isLoading}
              onClick={doRoute}
              className="mt-2"
            />
          </div>
        )}
      </div>
      )}

      {/* Birthday start dialog */}
      <Dialog
        header={t('remote_control.birthday.start')}
        visible={bdayDialog}
        onHide={() => setBdayDialog(false)}
        style={{ width: 'min(95vw, 420px)' }}
        modal
      >
        <div className="d-flex flex-column gap-3 pt-2">
          <div className="flex-column d-flex gap-1">
            <label className="small fw-semibold">{t('remote_control.birthday.guest')}</label>
            <InputText
              value={bdayForm.birthday_person_name}
              placeholder={t('remote_control.birthday.guest_ph')}
              onChange={(e) => setBdayForm({ ...bdayForm, birthday_person_name: e.target.value })}
            />
          </div>
          <div className="flex-column d-flex gap-1">
            <label className="small fw-semibold">{t('remote_control.birthday.audio_preset')}</label>
            <Dropdown
              value={bdayForm.template}
              options={audioOptions.length ? audioOptions : [
                { label: 'Arabic', value: 'arabic' },
                { label: 'English', value: 'english' },
                { label: 'Calm', value: 'calm' },
                { label: 'Party', value: 'party' },
              ]}
              onChange={(e) => setBdayForm({ ...bdayForm, template: e.value })}
              placeholder={t('remote_control.birthday.no_audio')}
            />
          </div>
          <div className="flex-column d-flex gap-1">
            <label className="small fw-semibold">{t('remote_control.birthday.duration_min')}</label>
            <InputNumber
              value={bdayForm.duration_minutes}
              onValueChange={(e) => setBdayForm({ ...bdayForm, duration_minutes: e.value })}
              min={1} max={60}
            />
          </div>
          <div className="flex-column d-flex gap-1">
            <label className="small fw-semibold">{t('remote_control.birthday.select_screens')}</label>
            {(screens ?? []).map((s) => (
              <label key={s.id} className="d-flex align-items-center gap-2">
                <Checkbox
                  checked={bdayForm.device_ids.includes(s.id)}
                  onChange={(e) => {
                    setBdayForm({
                      ...bdayForm,
                      device_ids: e.checked
                        ? [...bdayForm.device_ids, s.id]
                        : bdayForm.device_ids.filter((id) => id !== s.id),
                    });
                  }}
                />
                {s.name}
              </label>
            ))}
          </div>
          <div className="d-flex justify-content-end gap-2 mt-2">
            <Button
              label={t('buttons.cancel')}
              severity="secondary" outlined
              onClick={() => setBdayDialog(false)}
            />
            <Button
              label={t('remote_control.birthday.start_btn')}
              loading={startMut.isLoading}
              onClick={startBirthday}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
}
